import { t } from '@lingui/core/macro';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { participantProjectsControllerCancelApplication } from '@/api/core-api';
import { notify } from '@/components/base/notifications/notifications';
import { getClient } from '@/utils/get-api-client';

import { queryKeys } from './query-keys';

/**
 * Cancels a participant's application or participation. Owns the refresh so
 * every entry point (My Projects card, Active projects, the project page, the
 * `?cancel=true` email link) leaves the lists and the project page agreeing,
 * rather than each caller refreshing only what it happens to show.
 */
export function useCancelApplication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      userId,
    }: {
      projectId: string;
      userId: string;
    }) => {
      const client = getClient(userId);
      const response = await participantProjectsControllerCancelApplication({
        path: { projectId, userId },
        client,
      });

      if (response.error) {
        throw new Error('Failed to cancel application');
      }

      return { projectId, userId };
    },
    onSuccess: ({ projectId, userId }) => {
      notify({
        title: t`Application cancelled`,
        color: 'success',
      });
      for (const queryKey of [
        queryKeys.activeSurveys.byUserId(userId),
        queryKeys.appliedSurveys.byUserId(userId),
        queryKeys.pastSurveys.byUserId(userId),
        queryKeys.projects.detailBase(projectId),
      ]) {
        void queryClient.invalidateQueries({ queryKey: queryKey });
      }
    },
    onError: (error) => {
      notify({
        title: t`Failed to cancel application`,
        description: error.message,
        color: 'error',
      });
    },
  });
}
